/**
 * Settings → Email Sender tab (workspace-level).
 *
 * Follows the **default / override** hierarchy:
 *   • "Use Default Settings" checkbox at the top (checked by default).
 *   • When CHECKED → workspace inherits the super-admin platform default.
 *     Fields are visible but read-only, populated with the platform values
 *     so the admin can eyeball what is currently in effect.
 *   • When UNCHECKED → fields become editable; the workspace saves its own
 *     provider/credentials which then take precedence over the platform
 *     default for every outbound email from this workspace.
 *
 * Send Test Email is always available. When defaults are in use the test
 * is sent using the platform config; otherwise the workspace override is
 * used. The backend picks the right one automatically based on the saved
 * ``use_default`` flag.
 */
import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import PasswordInput from "@/components/ui/password-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle, EnvelopeSimple, Info, PaperPlaneTilt, WarningCircle,
  Lock,
} from "@phosphor-icons/react";

const EMPTY = {
  use_default: true,
  provider: "resend",
  from_name: "",
  from_email: "",
  reply_to: "",
  api_key: "",
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  smtp_use_tls: true,
};

export default function EmailSenderTab() {
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/email-sender");
      setRow(data);
      setForm({
        use_default: data.use_default !== false,
        provider: data.provider || "resend",
        from_name: data.from_name || "",
        from_email: data.from_email || "",
        reply_to: data.reply_to || "",
        api_key: "",
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_username: data.smtp_username || "",
        smtp_password: "",
        smtp_use_tls: data.smtp_use_tls !== false,
      });
      setTestResult(data.last_test_at ? {
        ok: !!data.last_test_ok,
        at: data.last_test_at,
        error: data.last_test_error,
      } : null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const usingDefault = form.use_default;
  const platformDefault = row?.platform_default;

  // When "Use default" is ON, the on-screen fields become read-only and
  // display whatever the platform has configured. When OFF, they show
  // the workspace's own override (persisted between edits).
  const displayed = useMemo(() => {
    if (usingDefault && platformDefault) {
      return {
        provider: platformDefault.provider || "resend",
        from_name: platformDefault.from_name || "",
        from_email: platformDefault.from_email || "",
        reply_to: platformDefault.reply_to || "",
      };
    }
    return {
      provider: form.provider,
      from_name: form.from_name,
      from_email: form.from_email,
      reply_to: form.reply_to,
    };
  }, [usingDefault, platformDefault, form.provider, form.from_name,
      form.from_email, form.reply_to]);

  const save = async () => {
    if (!usingDefault) {
      if (!form.from_email.trim()) return toast.error("From email is required");
      if (form.provider === "resend" && !row?.api_key_set && !form.api_key.trim()) {
        return toast.error("Enter a Resend API key");
      }
      if (form.provider === "smtp") {
        if (!form.smtp_host.trim()) return toast.error("SMTP host is required");
        if (!form.smtp_username.trim()) return toast.error("SMTP username is required");
        if (!row?.smtp_password_set && !form.smtp_password) {
          return toast.error("SMTP password is required");
        }
      }
    }
    setBusy(true);
    try {
      // Backend accepts a full payload; when use_default=true it stores
      // the override fields but won't use them at send-time.
      await api.put("/settings/email-sender", form);
      toast.success(usingDefault
        ? "Reverted to platform default settings."
        : "Workspace override saved. Send a test to confirm it works.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/email-sender/test", {});
      setTestResult({
        ok: !!data.ok,
        error: data.error,
        at: new Date().toISOString(),
        to: data.to,
        source: data.source,
      });
      if (data.ok) toast.success(`Test email sent to ${data.to}`);
      else toast.error("Test failed — see details below");
      await load();
    } catch (e) {
      const err = formatApiError(e);
      setTestResult({ ok: false, error: err, at: new Date().toISOString() });
      toast.error(err);
    } finally { setTesting(false); }
  };

  const isSmtp = displayed.provider === "smtp";
  const isResend = displayed.provider === "resend";
  const testDisabled = testing
    || (usingDefault && !platformDefault?.configured)
    || (!usingDefault && !row?.from_email);

  return (
    <div className="space-y-4 max-w-3xl" data-testid="email-sender-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2"><EnvelopeSimple size={20} className="text-primary" /></div>
            <div>
              <CardTitle>Email sender settings</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Powers every outbound email from this workspace — password resets,
                intake links, appointment reminders, receipts.
              </p>
            </div>
            {usingDefault ? (
              <Badge className="ml-auto bg-slate-100 text-slate-700 border-slate-200 gap-1" data-testid="sender-using-default">
                Using platform default
              </Badge>
            ) : (row?.verified && (
              <Badge className="ml-auto bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" data-testid="sender-verified">
                <CheckCircle size={12} weight="fill" /> Working
              </Badge>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ---------- Use Default toggle ----------
              Note: the checkbox is a *sibling* (not a child) of the click
              target so that browser's implicit "click label toggles input"
              doesn't fire alongside our explicit onChange — which caused a
              double-toggle regression in earlier iterations. */}
          <div
            className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3"
            data-testid="use-default-row"
          >
            <input
              id="use-default-checkbox"
              type="checkbox"
              checked={!!form.use_default}
              onChange={e => update("use_default", e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
              data-testid="use-default-checkbox"
            />
            <label htmlFor="use-default-checkbox" className="flex-1 space-y-0.5 cursor-pointer">
              <div className="text-sm font-medium">Use default settings</div>
              <div className="text-xs text-muted-foreground">
                {usingDefault
                  ? `This workspace inherits the platform default sender (${platformDefault?.from_email || "not configured yet — contact your platform admin"}). Uncheck to configure your own.`
                  : "This workspace uses its own custom sender. Re-check to revert to the platform default."}
              </div>
            </label>
          </div>

          {usingDefault && !platformDefault?.configured && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="platform-default-missing">
              The platform default sender is not configured yet. Ask your super admin
              to set it up on the Platform admin page, or uncheck &quot;Use default settings&quot; to
              configure this workspace&apos;s own sender.
            </div>
          )}

          {/* ---------- Provider dropdown ---------- */}
          <div className="space-y-1.5">
            <Label className={usingDefault ? "text-muted-foreground" : undefined}>
              Email service provider *
            </Label>
            <Select
              value={displayed.provider}
              onValueChange={(v) => update("provider", v)}
              disabled={usingDefault}
            >
              <SelectTrigger data-testid="sender-provider" aria-disabled={usingDefault}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="resend" data-testid="sender-provider-resend">Resend (recommended)</SelectItem>
                <SelectItem value="smtp" data-testid="sender-provider-smtp">SMTP (Gmail, Zoho, custom server)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className={usingDefault ? "text-muted-foreground" : undefined}>Sender name</Label>
              <Input
                readOnly={usingDefault}
                value={displayed.from_name}
                onChange={e => update("from_name", e.target.value)}
                placeholder="e.g. Khair Therapy Team"
                data-testid="sender-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className={usingDefault ? "text-muted-foreground" : undefined}>Sender email *</Label>
              <Input
                readOnly={usingDefault}
                type="email"
                value={displayed.from_email}
                onChange={e => update("from_email", e.target.value)}
                placeholder="noreply@yourdomain.com"
                data-testid="sender-email"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className={usingDefault ? "text-muted-foreground" : undefined}>Reply-to email (optional)</Label>
              <Input
                readOnly={usingDefault}
                type="email"
                value={displayed.reply_to}
                onChange={e => update("reply_to", e.target.value)}
                placeholder="reception@yourdomain.com"
                data-testid="sender-reply-to"
              />
              <p className="text-[11px] text-muted-foreground">Replies to your outbound emails will be routed here.</p>
            </div>
          </div>

          {usingDefault ? (
            <div className="rounded-md border bg-muted/30 p-3 flex items-start gap-2 text-xs text-muted-foreground" data-testid="default-locked-notice">
              <Lock size={14} className="mt-0.5 shrink-0" />
              <div>
                Provider credentials are held by the platform admin. To use a different
                key, uncheck <span className="font-medium">Use default settings</span> above.
              </div>
            </div>
          ) : (
            <>
              {isResend && (
                <div className="space-y-1.5 rounded-md border bg-muted/30 p-3">
                  <Label>Resend API key {row?.api_key_set ? <span className="text-emerald-700 font-normal text-[11px]">(saved · {row.api_key_hint})</span> : "*"}</Label>
                  <PasswordInput
                    value={form.api_key}
                    onChange={e => update("api_key", e.target.value)}
                    placeholder={row?.api_key_set ? "Leave blank to keep existing key" : "re_••••••••••••"}
                    data-testid="sender-api-key"
                    autoComplete="off"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Generate one at{" "}
                    <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                      resend.com/api-keys
                    </a>. Make sure the from-email domain is verified in Resend first.
                  </p>
                </div>
              )}

              {isSmtp && (
                <div className="space-y-3 rounded-md border bg-muted/30 p-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1.5 md:col-span-2">
                      <Label>SMTP host *</Label>
                      <Input
                        value={form.smtp_host}
                        onChange={e => update("smtp_host", e.target.value)}
                        placeholder="smtp.gmail.com"
                        data-testid="smtp-host"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Port *</Label>
                      <Input
                        type="number"
                        value={form.smtp_port}
                        onChange={e => update("smtp_port", parseInt(e.target.value || "587", 10))}
                        placeholder="587"
                        data-testid="smtp-port"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>SMTP username *</Label>
                      <Input
                        value={form.smtp_username}
                        onChange={e => update("smtp_username", e.target.value)}
                        placeholder="you@yourdomain.com"
                        data-testid="smtp-username"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label>SMTP password {row?.smtp_password_set ? <span className="text-emerald-700 font-normal text-[11px]">(saved)</span> : "*"}</Label>
                      <PasswordInput
                        value={form.smtp_password}
                        onChange={e => update("smtp_password", e.target.value)}
                        placeholder={row?.smtp_password_set ? "Leave blank to keep existing password" : "App password or SMTP secret"}
                        data-testid="smtp-password"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <input
                      id="smtp-tls" type="checkbox"
                      checked={!!form.smtp_use_tls}
                      onChange={e => update("smtp_use_tls", e.target.checked)}
                      data-testid="smtp-use-tls"
                    />
                    <label htmlFor="smtp-tls" className="cursor-pointer text-muted-foreground">
                      Upgrade the connection with STARTTLS (recommended for port 587).
                      Uncheck only if your server is plain-text (or use port 465 for implicit TLS).
                    </label>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button onClick={save} disabled={busy} data-testid="sender-save">
              {busy ? "Saving…" : (usingDefault ? "Save (use default)" : "Save override")}
            </Button>
            <Button
              onClick={runTest}
              disabled={testDisabled}
              variant="outline"
              className="gap-2"
              data-testid="sender-test"
            >
              <PaperPlaneTilt size={14} weight="fill" />
              {testing ? "Sending test…" : `Send test email to ${user?.email || "me"}`}
            </Button>
          </div>

          {testResult && (
            <div
              className={`rounded-md border p-3 text-sm ${testResult.ok
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-rose-200 bg-rose-50 text-rose-900"}`}
              data-testid={testResult.ok ? "sender-test-success" : "sender-test-error"}
            >
              <div className="flex items-start gap-2">
                {testResult.ok
                  ? <CheckCircle size={18} weight="fill" className="mt-0.5" />
                  : <WarningCircle size={18} weight="fill" className="mt-0.5" />
                }
                <div className="space-y-1">
                  <div className="font-medium">
                    {testResult.ok
                      ? `Test email sent successfully${testResult.to ? ` to ${testResult.to}` : ""}${testResult.source ? ` via ${testResult.source === "platform_default" ? "platform default" : "workspace override"}` : ""}.`
                      : "Test send failed"}
                  </div>
                  {testResult.error && !testResult.ok && (
                    <div className="text-xs font-mono whitespace-pre-wrap break-all">
                      {testResult.error}
                    </div>
                  )}
                  {testResult.at && (
                    <div className="text-[11px] opacity-75">
                      {new Date(testResult.at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info size={16} /> Domain deliverability tips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Emails from a domain you own are far less likely to land in spam.
            Configure SPF, DKIM and DMARC with your DNS provider before switching to a
            custom domain for the from-address.
          </p>
          <div className="rounded-md border p-3 text-xs bg-muted/40 space-y-2">
            <div><span className="font-mono font-medium">TXT @</span> <code>v=spf1 include:_spf.resend.com ~all</code> (or your provider&apos;s SPF include)</div>
            <div><span className="font-mono font-medium">TXT resend._domainkey</span> — DKIM value from your provider&apos;s dashboard</div>
            <div><span className="font-mono font-medium">TXT _dmarc</span> <code>v=DMARC1; p=none; rua=mailto:postmaster@yourdomain</code></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
